import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  Database,
  FileText,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Trash2,
} from "lucide-react";

export default function PrivacyPolicy() {
  const policySections = [
    {
      title: "Personal Data We Process",
      icon: FileText,
      items: [
        "Recruiter account details such as name, email address, authentication status, subscription plan, and workspace settings.",
        "Candidate CV files, extracted CV text, candidate names, email addresses, phone numbers, skills, experience signals, job titles, and imported attachment metadata.",
        "Job descriptions, recruiter notes, shortlist decisions, candidate search queries, match scores, AI-generated summaries, and scan history.",
        "Gmail import connection details needed to import candidate attachments, plus import logs that help recruiters review what was added.",
        "API keys, API request history, endpoint usage, rate-limit counters, success or failure status, and basic technical logs used to operate the service.",
      ],
    },
    {
      title: "How RecruitGlance Uses This Data",
      icon: Database,
      items: [
        "To parse CVs, extract skills and contact information, rank candidates against job descriptions, and show recruiter-friendly evidence.",
        "To power AI shortlist summaries, semantic candidate search, duplicate detection, Gmail candidate import, dashboards, and API access.",
        "To protect the platform through authentication, account permissions, usage limits, file validation, API rate limits, and operational logging.",
        "To help recruiters manage candidates through saved candidates, notes, shortlist status, deletion flows, and profile/account settings.",
      ],
    },
    {
      title: "Controller And Processor Roles",
      icon: ShieldCheck,
      items: [
        "For candidate CVs, job descriptions, recruiter notes, shortlist decisions, and hiring workflow data, the recruiter or customer organization normally acts as the data controller because they decide why and how candidate data is used.",
        "RecruitGlance acts as a service provider/data processor for customer recruitment data and processes that data only to provide, secure, maintain, and improve the contracted recruitment service.",
        "Customers are responsible for ensuring they have a lawful basis to upload, import, scan, rank, store, and review candidate data in RecruitGlance.",
        "Candidates should contact the recruiter or organization that uploaded their CV to exercise privacy rights. RecruitGlance supports customers in handling lawful access, correction, deletion, or objection requests where required.",
        "Before using RecruitGlance with real candidate CVs at scale, customers should have an appropriate data processing agreement or equivalent written terms in place.",
      ],
    },
    {
      title: "AI Processing",
      icon: ShieldCheck,
      items: [
        "RecruitGlance may send relevant CV and job-description excerpts to an AI provider to generate summaries, strengths, gaps, ranking reasons, and recruiter insights.",
        "RecruitGlance uses large language model AI services including OpenAI GPT to generate candidate summaries, scoring signals, and match explanations.",
        "AI output is decision support only. Recruiters should review the original CV and make the final hiring decision themselves.",
        "Recruiters should upload or import only candidate data they are authorized to process for recruitment purposes.",
        "RecruitGlance does not use customer CVs or candidate personal data to train general-purpose AI models. Product feedback may be used to improve quality only in aggregated, anonymized, or customer-approved form.",
      ],
    },
    {
      title: "EU AI Act And Human Oversight",
      icon: ShieldCheck,
      items: [
        "RecruitGlance is designed as recruiter decision support for candidate screening, ranking, and evidence review. It is not intended to make fully automated hiring, rejection, interview, compensation, or employment eligibility decisions.",
        "RecruitGlance uses large language model AI services including OpenAI GPT to generate candidate summaries, scoring signals, and match explanations.",
        "Recruiters remain responsible for reviewing the original CV, job requirements, score breakdown, AI explanations, and any missing or uncertain evidence before making a hiring decision.",
        "RecruitGlance provides score breakdowns, detected signals, gaps, notes, scan history, and recruiter-controlled decisions to support transparency and human oversight.",
      ],
    },
    {
      title: "Email Import",
      icon: Mail,
      items: [
        "Email import is optional and starts only after the recruiter gives consent and completes Google or Microsoft OAuth permission.",
        "RecruitGlance uses mailbox access only to search recent Inbox emails for the selected job and configured import window.",
        "RecruitGlance looks for job-related PDF CV attachments and stages matching candidates for recruiter approval before saving them to the candidate library.",
        "Staged candidates can be approved or rejected by the recruiter. Rejected staged candidates are not saved as candidate records from that approval queue.",
        "Imported candidates should be reviewed by the recruiter. If a wrong attachment is approved and saved, the recruiter can delete the candidate record from the application.",
        "Mailbox access should use the minimum permissions required for candidate import and can be revoked from the recruiter's Google or Microsoft account settings when no longer needed.",
        "If the recruiter enables candidate decision emails, RecruitGlance uses the connected Gmail or Outlook mailbox to send shortlist or rejection messages from the recruiter's mailbox. Candidates should not receive decision emails from a RecruitGlance-owned domain.",
        "RecruitGlance does not use mailbox data for advertising and does not make final hiring decisions from mailbox content.",
      ],
    },
    {
      title: "Google User Data Sharing And Disclosure",
      icon: Mail,
      items: [
        "RecruitGlance uses Google user data only to provide recruiter-requested Gmail features: importing job-related PDF CV attachments from recent Inbox emails and sending recruiter-approved candidate decision emails from the recruiter's Gmail account.",
        "RecruitGlance may access Gmail messages, message metadata, sender/date details, message body snippets, and PDF attachments only to identify candidate CVs related to the selected job and prepare them for recruiter review.",
        "RecruitGlance does not sell Google user data and does not use Google user data for advertising, retargeting, credit decisions, surveillance, or unrelated analytics.",
        "RecruitGlance does not share, transfer, or disclose Google user data to third parties except where necessary to provide and secure the visible recruiter-facing features, including hosting, database, logging, mailbox integration, and AI processing providers; where required by law; or with the user's explicit consent.",
        "RecruitGlance may use OpenAI API services to generate recruiter-facing candidate summaries, scoring signals, skill evidence, and match explanations from CV and job-related content. Google email data is used only for the recruiter-requested hiring workflow.",
        "Human access to Google user data is restricted and may occur only when necessary for security, debugging, legal compliance, or support requested by the user.",
        "Recruiters can revoke Gmail access from their Google account settings and can request deletion of imported candidate data from RecruitGlance.",
      ],
    },
    {
      title: "Security And Access",
      icon: LockKeyhole,
      items: [
        "RecruitGlance uses authentication, HTTPS, CORS restrictions, file-size validation, API keys, rate limits, and user-scoped database access to reduce risk.",
        "Candidate data is not sold. Data may be processed by necessary service providers such as hosting, database, email delivery, payment, logging, or AI infrastructure.",
        "Service providers may include hosting and database infrastructure, frontend hosting, email delivery, payment processing, mailbox integrations, AI infrastructure, and operational logging tools needed to run the service.",
        "Where service providers process data outside the customer's region, customers should review the applicable transfer mechanism, contractual safeguards, and subprocessor terms before production use.",
        "No online system can be guaranteed fully secure, so recruiters should avoid uploading unnecessary sensitive information and should remove records that are no longer needed.",
      ],
    },
    {
      title: "Retention And Deletion",
      icon: Trash2,
      items: [
        "Candidate records, CV text, scan results, notes, and logs are kept while the recruiter account needs them for recruitment workflows.",
        "Pilot workspace data is scheduled for deletion one day after the 2-day pilot period ends. This includes uploaded CVs, candidate records, scan results, AI outputs, vectors, mailbox connections, jobs, and related recruitment data.",
        "Operational, security, billing, and API logs may be retained for a limited period where needed for fraud prevention, service reliability, billing evidence, or legal compliance.",
        "Recruiters should remove incorrect imports, wrong attachments, duplicate records, and candidate data that is no longer needed for a live recruitment workflow.",
      ],
    },
    {
      title: "Pilot Use With Real CVs",
      icon: FileText,
      items: [
        "Pilot users may test RecruitGlance with real CVs only if they are authorized to process those CVs for recruitment evaluation.",
        "Pilot access is intended for limited validation of the product, not long-term storage of candidate data.",
        "When a pilot ends, RecruitGlance keeps the provisioned user login record but removes the pilot workspace data after the cleanup window.",
        "The customer remains responsible for notifying candidates or obtaining consent where required by applicable recruitment, privacy, or employment rules.",
      ],
    },
    {
      title: "Contact",
      icon: Mail,
      items: [
        "Company/contact name: RecruitGlance.",
        "For privacy, support, or legal questions, contact RecruitGlance at recruitglance@gmail.com.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Navbar />

      <main className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              RecruitGlance Privacy Policy
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              Candidate data must be handled with care.
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600">
              This policy explains what data RecruitGlance processes, why it is used,
              and how recruiters should manage candidate information inside the
              platform, including uploaded CVs, recruiter notes, AI-assisted
              screening outputs, account records, and billing information.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Last updated: August 2, 2026
            </p>
          </div>

          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm leading-6 text-emerald-900">
            RecruitGlance is designed for recruiter-led screening. Recruiters remain
            responsible for using candidate data fairly, lawfully, and only for
            legitimate hiring purposes.
          </div>

          <div className="mt-8 grid gap-5">
            {policySections.map(({ title, icon: Icon, items }) => (
              <section
                key={title}
                className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 text-white">
                    <Icon size={20} />
                  </span>
                  <h2 className="text-xl font-semibold text-slate-950">
                    {title}
                  </h2>
                </div>
                <ul className="mt-5 space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <section className="mt-8 rounded-lg border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
            <h2 className="text-xl font-semibold">Recruiter Responsibility</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Recruiters are responsible for making sure they have a lawful
              reason to upload, import, scan, store, and evaluate candidate CVs.
              RecruitGlance helps organize recruitment data, but it does not replace
              the recruiter&apos;s responsibility to follow applicable privacy,
              employment, and anti-discrimination rules.
            </p>
          </section>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900"
            >
              Back to Home
            </Link>
            <Link
              to="/register"
              className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
            >
              Create Account
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
